import { useAuth0 } from '@auth0/auth0-react'
import { ReactNode, useEffect } from 'react'
import { useAppDispatch } from '@/hooks/storeHooks/storehooks'
import { fetchMenuItems } from '@/services/apis/api'
import { useBrandingInit } from '@/hooks/useBrandingInit'
import {
  setAppLoader,
  setMenuItems,
  setPaths,
  setPerms,
  setWidgets,
} from '@/redux-store/slices/menuItemSlice/menuItemSlice'
import { AxiosResponse } from 'axios'
import {
  extractAllowedPaths,
  extractPagePermissions,
  extractWidgetsWithExpandedView,
  MenuItem,
} from '@/lib/utils'
import { MenusType } from '@/services/types/api.types'
import { setAuthInterceptor } from '@/services/axiosInstance/axiosInstance'
import * as Sentry from '@sentry/react'

type AuthUserProps = {
  children: ReactNode
}

type Widget = {
  key: string
  name: string
  value?: boolean
}

type FetchMenusResponse = AxiosResponse<MenusType>

export const AuthUser = ({ children }: AuthUserProps) => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    logout,
    loginWithRedirect,
    isLoading,
    user,
  } = useAuth0()
  const dispatch = useAppDispatch()
  const userName = user?.sub

  // Apply persisted branding (navbar colour, favicon, logos, tab title) on startup
  useBrandingInit()

  useEffect(() => {
    if (isAuthenticated && user) {
      Sentry.setUser({
        id: user.sub,
        username: user.name,
        email: user.email,
      })
    }

    if (!isAuthenticated) {
      Sentry.setUser(null)
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('new_interface_popup_handled_')) {
          sessionStorage.removeItem(key)
        }
      })
      dispatch(setAppLoader(false))
      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname + window.location.search,
        },
      })
      return
    }
    if (isAuthenticated) {
      setAuthInterceptor(async () => {
        try {
          return await getAccessTokenSilently()
        } catch (err) {
          console.error('Token refresh failed, logging out:', err)
          logout({ logoutParams: { returnTo: window.location.origin } })
          return ''
        }
      })

      const fetchAllData = async () => {
        const menuItems = (await fetchMenuItems()) as FetchMenusResponse
        dispatch(setAppLoader(false))

        if (menuItems.status === 200) {
          const path = extractAllowedPaths(menuItems.data as MenuItem[])
          const result = extractWidgetsWithExpandedView(menuItems.data)
          const permissions = extractPagePermissions(menuItems.data)
          dispatch(setPerms(permissions))

          if (result?.Home) {
            const widgets = result?.Home
            const downtimeIndex = widgets.findIndex(
              w => w.key === 'prevented_downtime',
            )
            const latencyIndex = widgets.findIndex(
              w => w.key === 'latency_metrics',
            )

            if (downtimeIndex > -1 && latencyIndex > -1) {
              const [downtimeWidget] = widgets.splice(downtimeIndex, 1)
              widgets.splice(latencyIndex + 1, 0, downtimeWidget)
            }

            result.Home = widgets
          }

          if (userName) {
            Object.keys(result).forEach(pageName => {
              const storageKey = `${userName}_${pageName}_widget_order`

              const stored = localStorage.getItem(storageKey)

              if (!stored) return

              const parsed = JSON.parse(stored)

              if (!parsed?.widgetKeys?.length) return

              const orderedWidgets = parsed.widgetKeys
                .map((key: string) =>
                  result[pageName]?.find((w: Widget) => w.key === key),
                )
                .filter(Boolean)

              const missingWidgets =
                result[pageName]?.filter(
                  (w: Widget) => !parsed.widgetKeys.includes(w.key),
                ) || []

              result[pageName] = [...orderedWidgets, ...missingWidgets]
            })
          }
          console.log(result, 'efjvbefgvbeffberu')
          console.log('path:', path, 'menuItems:', menuItems.data)

          dispatch(setWidgets(result))
          dispatch(setMenuItems(menuItems.data))
          dispatch(setPaths(path))
        } else {
          console.error('Error fetching data:', menuItems)
          dispatch(setAppLoader(false))
        }
      }

      fetchAllData()
    }
    return () => {
      if (!isAuthenticated) {
        dispatch(setAppLoader(false))
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    logout,
    loginWithRedirect,
    dispatch,
  ])

  // if (isLoading) {
  //   return <div>Loading...</div>
  // }

  if (!isAuthenticated) {
    return null
  }

  return <div className='h-full'>{children}</div>
}
